import {
  convexQuery,
  useConvexAction,
  useConvexMutation,
} from "@convex-dev/react-query";
import { useMutation, useQuery } from "@tanstack/react-query";
import type { Id } from "@teachloop/backend/_generated/dataModel";
import { useFormatDate } from "@/elements/hooks/use-format-date";
import * as DocumentPicker from "expo-document-picker";
import { Redirect, useRouter } from "expo-router";
import React, { Fragment, useState } from "react";
import { Alert, Pressable, ScrollView, Text, View } from "react-native";
import Toast from "react-native-toast-message";
import ClockIcon from "@/assets/svgs/clock-icon";
import RemoteIcon from "@/assets/svgs/remote-icon";
import ReportIcon from "@/assets/svgs/report-icon";
import SubjectIcon from "@/assets/svgs/subject-icon";
import UserIcon from "@/assets/svgs/user-icon";
import LessonSubmittedModal from "@/elements/components/modals/lesson-submitted-modal";
import ResponsiveContent from "@/elements/components/shared/responsive-content";
import { AppBar } from "@/elements/components/ui/app-bar";
import { Avatar } from "@/elements/components/ui/avatar";
import { Button } from "@/elements/components/ui/button";
import { api } from "@/elements/components/utils/convex-api";
import goBack from "@/elements/components/utils/go-back";
import { contactSupport } from "@/elements/components/utils/helpers";
import useNavigationData from "@/elements/hooks/use-navigation-data";

type FileMeta = {
  uri: string;
  name: string;
  mimeType: string;
  size: number;
};

function StartInPersonLessonScreen() {
  const fmt = useFormatDate();
  const router = useRouter();
  const session = useNavigationData<
    NonNullable<typeof api.tutor.home.queries.getSessions._returnType>[number]
  >("/lesson-recording/start-in-person-lesson");

  // File upload state
  const [file, setFile] = useState<FileMeta | null>(null);
  const [isLessonSubmittedModalVisible, setIsLessonSubmittedModalVisible] =
    useState(false);

  const { mutateAsync: setSessionInProgress } = useMutation({
    mutationFn: useConvexMutation(
      api.tutor.home.mutations.setSessionInProgress,
    ),
  });

  // Check if upload is allowed for completed sessions
  const { data: canUpload = false } = useQuery({
    ...convexQuery(api.tutor.home.queries.canUploadSessionRecording, {
      sessionId: session?.sessionId as Id<"sessions">,
    }),
    enabled: !!session?.sessionId && session.status === "completed",
  });

  const { mutateAsync: generateVoiceUploadUrl } = useMutation({
    mutationFn: useConvexAction(api.tutor.home.actions.generateVoiceUploadUrl),
  });

  const { mutateAsync: createSessionRecording } = useMutation({
    mutationFn: useConvexMutation(
      api.tutor.home.mutations.createInPersonSessionRecording,
    ),
  });

  const base64ToBlob = (base64Uri: string) => {
    const [meta, data] = base64Uri.split(",");
    const mime = meta?.match(/:(.*?);/)?.[1];
    if (!mime) throw new Error("Invalid MIME type");
    const binary = atob(data);
    const array = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) {
      array[i] = binary.charCodeAt(i);
    }
    return new Blob([array], { type: mime });
  };

  // Upload mutation
  const uploadMutation = useMutation({
    mutationFn: async () => {
      if (!file) throw new Error("No file to upload");

      const fileUri = file.uri;
      let blob;

      if (fileUri.startsWith("data:")) {
        blob = base64ToBlob(fileUri);
      } else if (fileUri.startsWith("blob:") || fileUri.startsWith("http")) {
        const res = await fetch(fileUri);
        blob = await res.blob();
      } else if (file instanceof File) {
        blob = file;
      } else {
        throw new Error("Unsupported file URI format on this platform");
      }

      const size = file.size || blob.size;
      const name = file.name || `lesson-${Date.now()}.m4a`;
      const mimeType = file.mimeType || "audio/mp4";

      const uploadData = await generateVoiceUploadUrl({
        fileName: name,
        fileSize: size,
        mimeType,
        sessionId: session.sessionId as Id<"sessions">,
      });

      const uploadRes = await fetch(uploadData.uploadUrl, {
        method: "PUT",
        body: blob,
        headers: { "Content-Type": mimeType },
      });

      if (!uploadRes.ok) throw new Error("Upload failed");

      await createSessionRecording({
        sessionId: session.sessionId as Id<"sessions">,
        fileName: uploadData.fileName,
        source: "app",
        type: "audio",
      });

      setIsLessonSubmittedModalVisible(true);
    },
    onSuccess: () => {
      setFile(null);
      Toast.show({ type: "success", text1: "Upload complete" });
    },
    onError: () =>
      Alert.alert(
        "Upload Failed",
        "Failed to upload recording. Please try again.",
      ),
  });

  const pickFile = async () => {
    const result = await DocumentPicker.getDocumentAsync({
      type: [
        "audio/mpeg",
        "audio/mp3",
        "audio/mp4",
        "audio/x-m4a",
        "audio/wav",
        "audio/webm",
        "audio/aac",
      ],
      multiple: false,
      copyToCacheDirectory: true,
    });
    if (!result.canceled && result.assets[0]) {
      const f = result.assets[0];
      setFile({
        uri: f.uri,
        name: f.name,
        mimeType: f.mimeType || "audio/mp4",
        size: f.size || 0,
      });
    }
  };

  const onStartInPersonLesson = async () => {
    if (session.status === "scheduled") {
      await setSessionInProgress({ sessionId: session.sessionId });
    }
    router.push(`/(app)/lesson-recording/${session.sessionId}`);
  };

  if (!session) {
    return <Redirect href="/" />;
  }

  return (
    <Fragment>
      {session && (
        <View className="flex-1 bg-teachLoop-creamBase">
          <AppBar>
            <AppBar.Icon onPress={goBack} />
          </AppBar>
          <ScrollView showsVerticalScrollIndicator={false}>
            <ResponsiveContent>
              <View className="bg-teachLoop-creamBase flex-1">
                <View className="px-8 bg-teachLoop-creamBase">
                  <View className="gap-8">
                    <Text className="font-zilla-slab-medium font-medium text-[28px] leading-[1.2] text-black">
                      Start lesson.
                    </Text>

                    <View className="flex-row items-center gap-4">
                      <Avatar size="md">
                        <Avatar.Content
                          label={session.student.name}
                          source={
                            session.student.imageUrl
                              ? { uri: session.student.imageUrl }
                              : undefined
                          }
                          labelSize="md"
                          size="md"
                        />
                      </Avatar>
                      <View className="flex-1">
                        <Text className="font-geologica-medium font-medium text-black text-xl leading-[1.5]">
                          {session.student.name}
                        </Text>
                      </View>
                    </View>

                    <View className="flex-row items-center gap-[6px]">
                      {session.deliveryMethod === "in_person" ? (
                        <UserIcon
                          className="size-6"
                          pathClassName={"stroke-gray-800"}
                        />
                      ) : (
                        <RemoteIcon
                          className="size-6"
                          pathClassName={"stroke-gray-800"}
                        />
                      )}
                      <Text
                        className={`font-geologica-medium font-medium text-gray-800 text-base leading-[1.5]`}
                      >
                        {session.deliveryMethod === "in_person"
                          ? "In person"
                          : "Remote"}
                      </Text>
                    </View>
                    <View className="gap-2">
                      <View className="flex-row items-center gap-[6px]">
                        <SubjectIcon
                          className="size-6"
                          pathClassName={"stroke-gray-800"}
                        />
                        <Text
                          className={`font-geologica-medium font-medium text-gray-800 text-base leading-[1.5]`}
                        >
                          {session.subjectName}
                        </Text>
                      </View>
                      <Text
                        className={`font-geologica-regular font-normal text-black text-base leading-[1.5]`}
                      >
                        {"  "}
                        &bull; {session.yearGroupName}
                      </Text>
                    </View>

                    <View className="gap-2">
                      <View className="flex-row items-center gap-[6px]">
                        <ClockIcon
                          className="size-6"
                          pathClassName={"stroke-gray-800"}
                        />
                        <Text
                          className={`font-geologica-medium font-medium text-gray-800 text-base leading-[1.5]`}
                        >
                          Time
                        </Text>
                      </View>
                      <Text
                        className={`font-geologica-regular font-normal text-black text-base leading-[1.5]`}
                      >
                        {fmt(session.startTime, "do MMMM")}{" "}
                        {fmt(session.startTime, "HH:mm")} -{" "}
                        {fmt(session.endTime, "HH:mm")}
                      </Text>
                    </View>

                    {session.tutorNotes && (
                      <View className="gap-2">
                        <Text className="font-geologica-medium font-medium text-gray-800 text-base leading-[1.5]">
                          Tutor Notes
                        </Text>
                        <Text className="font-geologica-regular font-normal text-black text-base leading-[1.5]">
                          {session.tutorNotes}
                        </Text>
                      </View>
                    )}

                    {session.parentNotes && (
                      <View className="gap-2">
                        <Text className="font-geologica-medium font-medium text-gray-800 text-base leading-[1.5]">
                          Parent Notes
                        </Text>
                        <Text className="font-geologica-regular font-normal text-black text-base leading-[1.5]">
                          {session.parentNotes}
                        </Text>
                      </View>
                    )}

                    <View className="flex-row justify-between items-center">
                      <View className="flex-1">
                        <Pressable
                          className="flex-row items-center gap-[6px] active:opacity-80"
                          onPress={contactSupport}
                        >
                          <ReportIcon
                            className="size-4"
                            pathClassName={"stroke-teachLoop-accentPrimary"}
                          />
                          <Text
                            className={`font-geologica-medium font-medium text-teachLoop-accentPrimary text-base leading-[1.5]`}
                          >
                            Report a problem
                          </Text>
                        </Pressable>
                      </View>

                      {(() => {
                        const fifteenMinutesAfterStart =
                          session.startTime + 15 * 60 * 1000;
                        const canReportNoShow =
                          Date.now() >= fifteenMinutesAfterStart;

                        return (
                          <View className="gap-1 flex-1 items-end">
                            <Pressable
                              className={`flex-row items-center gap-[6px] ${canReportNoShow ? "active:opacity-80" : ""}`}
                              onPress={
                                canReportNoShow
                                  ? () => {
                                      router.push(
                                        `/report-student-no-show?lessonId=${session.sessionId}`,
                                      );
                                    }
                                  : undefined
                              }
                              disabled={!canReportNoShow}
                            >
                              <ReportIcon
                                className="size-4"
                                pathClassName={
                                  canReportNoShow
                                    ? "stroke-teachLoop-accentPrimary"
                                    : "stroke-gray-400"
                                }
                              />
                              <Text
                                className={`font-geologica-medium font-medium text-base leading-[1.5] ${
                                  canReportNoShow
                                    ? "text-teachLoop-accentPrimary"
                                    : "text-gray-400"
                                }`}
                              >
                                Report no show
                              </Text>
                            </Pressable>

                            {!canReportNoShow && (
                              <Text className="font-geologica-regular font-normal text-gray-400 text-xs leading-[1.5] text-right">
                                Available 15 minutes after start time
                              </Text>
                            )}
                          </View>
                        );
                      })()}
                    </View>

                    {session.status === "scheduled" && (
                      <View className="items-end">
                        <Button
                          size="sm"
                          outline
                          onPress={() => {
                            router.push(
                              `/(app)/cancel-lesson?lessonId=${session.sessionId}`,
                            );
                          }}
                        >
                          <Button.Text size="sm" intent="primary">
                            Cancel lesson
                          </Button.Text>
                        </Button>
                      </View>
                    )}
                  </View>
                  <View className="h-3" />

                  {session.status === "completed" && canUpload && (
                    <View className="gap-4">
                      <View
                        className="bg-white p-6 rounded-[17px] shadow-hard-3 gap-4"
                        style={{
                          shadowColor: "#000",
                          shadowOffset: { width: 0, height: 1 },
                          shadowOpacity: 0.2,
                          shadowRadius: 2,
                          elevation: 2,
                        }}
                      >
                        <Text className="font-geologica-medium font-medium text-gray-800 text-lg leading-[1.5]">
                          Upload Recording
                        </Text>
                        <Text className="font-geologica-regular font-normal text-gray-600 text-base leading-[1.5]">
                          Upload an audio recording of this completed lesson to
                          generate transcripts and lesson reports.
                        </Text>

                        {file && (
                          <View className="bg-teachLoop-creamLight p-4 rounded-lg border border-teachLoop-creamDark">
                            <Text className="font-geologica-medium font-medium text-gray-800 text-base leading-[1.5]">
                              Selected File:
                            </Text>
                            <Text className="font-geologica-regular font-normal text-gray-600 text-sm leading-[1.5]">
                              {file.name}
                            </Text>
                            <Text className="font-geologica-regular font-normal text-gray-500 text-xs leading-[1.5]">
                              {(file.size / (1024 * 1024)).toFixed(1)} MB
                            </Text>
                          </View>
                        )}

                        <View className="gap-3">
                          <Button
                            size="md"
                            outline
                            onPress={pickFile}
                            disabled={uploadMutation.isPending}
                          >
                            <Button.Text size="md" intent="primary">
                              {file ? "Change File" : "Pick Recording"}
                            </Button.Text>
                          </Button>

                          {file && (
                            <Button
                              size="md"
                              onPress={() => uploadMutation.mutate()}
                              disabled={uploadMutation.isPending}
                            >
                              <Button.Text size="md" intent="light">
                                {uploadMutation.isPending
                                  ? "Uploading..."
                                  : "Upload Recording"}
                              </Button.Text>
                            </Button>
                          )}
                        </View>
                      </View>
                    </View>
                  )}
                </View>

                {session.status === "in_progress" && (
                  <View className="gap-4">
                    <Text
                      className={`font-geologica-regular font-normal text-teachLoop-textPrimary text-base leading-[1.5] text-center`}
                    >
                      Press start to record the lesson and generate lesson
                      reports.
                    </Text>

                    <View className="pb-10 pt-10 items-center gap-4">
                      <View className="items-center">
                        <View className="border-2 border-teachLoop-creamDark rounded-full p-1">
                          <Pressable
                            className="bg-red-500 h-12 w-12 rounded-full"
                            onPress={onStartInPersonLesson}
                          />
                        </View>
                      </View>

                      <Text className="font-geologica-medium font-medium text-black text-base leading-[1.5]">
                        Start lesson
                      </Text>
                    </View>
                  </View>
                )}
              </View>
            </ResponsiveContent>
          </ScrollView>
        </View>
      )}

      <LessonSubmittedModal
        isVisible={isLessonSubmittedModalVisible}
        onClose={() => {
          setIsLessonSubmittedModalVisible(false);
          router.replace("/(app)/home");
        }}
      />
    </Fragment>
  );
}

export default StartInPersonLessonScreen;
